type SubjectLike = {
  subject_id: string;
  name: string;
};

type SubjectOptionLike = {
  id: string;
  name: string;
};

const LEVEL_NAME_RE = /\b(?:form|grade)\s*\d+\b/gi;

const toTitleCase = (value: string): string =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

const stripLevelTokens = (value: string): string =>
  value
    .replace(LEVEL_NAME_RE, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

export const levelLabelFromSubjectId = (subjectId: string): string | null => {
  const formMatch = subjectId.match(/(?:_form_|(?:^|[-_])form_?)(\d+)/i);
  if (formMatch?.[1]) return `Form ${formMatch[1]}`;
  const shortFormMatch = subjectId.match(/(?:^|[-_])f(\d+)$/i);
  if (shortFormMatch?.[1]) return `Form ${shortFormMatch[1]}`;

  const gradeMatch = subjectId.match(/(?:_grade_|(?:^|[-_])grade_?)(\d+)/i);
  if (gradeMatch?.[1]) return `Grade ${gradeMatch[1]}`;
  const shortGradeMatch = subjectId.match(/(?:^|[-_])g(\d+)$/i);
  if (shortGradeMatch?.[1]) return `Grade ${shortGradeMatch[1]}`;
  return null;
};

export const baseSubjectNameFromId = (subjectId: string): string => {
  const normalized = subjectId
    .replace(/_form_\d+/gi, '')
    .replace(/_grade_\d+/gi, '')
    .replace(/(?:[-_])form_?\d+$/gi, '')
    .replace(/(?:[-_])grade_?\d+$/gi, '')
    .replace(/(?:[-_])[fg]\d+$/gi, '')
    .replace(/_/g, ' ')
    .trim();
  return toTitleCase(normalized);
};

const computeBaseName = (name: string, id: string): string => {
  const stripped = stripLevelTokens(name);
  if (stripped) return stripped;
  return baseSubjectNameFromId(id);
};

export const buildSubjectDisplayMap = (subjects: SubjectLike[]): Record<string, string> => {
  const baseById: Record<string, string> = {};
  const counts: Record<string, number> = {};

  for (const subject of subjects) {
    const base = computeBaseName(subject.name, subject.subject_id);
    baseById[subject.subject_id] = base;
    counts[base] = (counts[base] || 0) + 1;
  }

  const out: Record<string, string> = {};
  for (const subject of subjects) {
    const base = baseById[subject.subject_id];
    const needsLevel = (counts[base] || 0) > 1;
    const level = levelLabelFromSubjectId(subject.subject_id);
    out[subject.subject_id] = needsLevel && level ? `${base} (${level})` : base;
  }
  return out;
};

export const buildSubjectOptionDisplayMap = (options: SubjectOptionLike[]): Record<string, string> => {
  const asSubjects = options.map((option) => ({ subject_id: option.id, name: option.name }));
  return buildSubjectDisplayMap(asSubjects);
};

